import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {useSnackbar} from 'notistack';
import {useNavigate} from 'react-router';
import {Helmet} from 'react-helmet';
import {Chart, BarController, LineController, CategoryScale, LinearScale,
    BarElement, PointElement, LineElement, Legend,
    Tooltip as ChartTooltip} from 'chart.js';
import {Box, Card, Container, Divider, IconButton, Skeleton, Stack, styled, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs,
    Tooltip, Typography, useMediaQuery, useTheme} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ContactsIcon from '@mui/icons-material/Contacts';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {useBusyCallback} from '../../util/react_hooks.js';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import api from '../../util/api.js';
import {useUser} from '../../util/user.js';
import {downloadBlob, formatDate, formatDateIsrael, formatTime, SIMONA_USER_ID} from '../../util/util.js';
import CONFIG from '../../config.js';
import {withTitle} from '../../title.js';
import withMainContainer from '../../components/WithMainContainer.js';
import {withPermGuard} from '../../components/PermGuard.js';
import RadioCards from '../../components/RadioCards.js';
import SCButton from '../../components/SCButton.js';
import SCTextField from '../../components/SCTextField.js';
import {withLoginRequire} from '../../components/Login.js';
import {closeModal, openModal} from '../../components/Modals.js';
import {WEEK} from '../../util.js';
import {API_ERRORS_HANDLED, toastApiErrors} from '../../util/api_util.js';
import SkeletonWrap from '../../components/SkeletonWrap.js';

Chart.register(
    BarController,
    LineController,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    ChartTooltip,
    Legend,
);

const MONTH_NAMES = [
    'ינואר',
    'פברואר',
    'מרץ',
    'אפריל',
    'מאי',
    'יוני',
    'יולי',
    'אוגוסט',
    'ספטמבר',
    'אוקטובר',
    'נובמבר',
    'דצמבר',
];

const useStudentSettings = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['studentSettings'],
        queryFn: ()=>api.tarotmail.student.get(),
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });
    return [data?.studentSettings, isLoading];
};

const useCustomers = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['customers'],
        queryFn: ()=>api.tarotmail.student.customers(),
    });
    return [data?.customers, isLoading];
};

const useOrders = (opt={})=>{
    let {data, isLoading} = useQuery({
        queryKey: ['tarotmail_orders_student', opt],
        queryFn: ()=>api.tarotmail.student.getOrders(opt),
    });
    return [data, isLoading];
};

const useEvents = ()=>{
    let {data, isLoading} = useQuery({
        queryKey: ['tarotmail_events'],
        queryFn: ()=>api.tarotmail.student.getEvents(),
    });
    return [data, isLoading];
};

const useMailStatus = id=>{
    let {data, isLoading} = useQuery({
        queryKey: ['tarotmail_mail_status', id],
        queryFn: ()=>api.tarotmail.student.mailStatus(id),
        enabled: !!id,
    });
    return [data, isLoading];
};

const MailStatusModal = props=>{
    let {customer} = props;
    let [data, loading] = useMailStatus(customer?.id);
    return <Stack direction="column" gap={1} justifyContent="center" alignItems="center">
      <SkeletonWrap skeleton={loading}>
        <Typography fontWeight="bold">
          סה"כ נשלחו: {data?.sent||0}
        </Typography>
      </SkeletonWrap>
      {(data?.lastStatus||loading)&&<SkeletonWrap skeleton={loading}>
        <Typography>
          המייל האחרון{' '}
          <Typography component="span" color={data?.lastStatus=='delivered' ? 'success.main' : 'error'}>
            {data?.lastStatus=='delivered' ? 'התקבל' : 'נכשל'}
          </Typography>
        </Typography>
      </SkeletonWrap>}
    </Stack>;
};

const EmailListTableHeader = props=>{
    let {children, orderable, order, changeOrder, id, ...rest} = props;
    let [showOrderButton, setShowOrderButton] = useState();
    return <TableCell align="right"
      onMouseEnter={()=>setShowOrderButton(true)}
      onMouseLeave={()=>setShowOrderButton(false)}
      {...rest}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography fontWeight="bold" marginTop={1} marginBottom={1}>
          {children}
        </Typography>
        {orderable&&(showOrderButton||order.column==id)&&<IconButton
          size="small" onClick={()=>changeOrder(id)}
          color={order.column==id ? 'primary' : undefined}>
          {order.column==id&&order.direction=='desc' ? <ArrowUpwardIcon/> : <ArrowDownwardIcon/>}
        </IconButton>}
      </Stack>
    </TableCell>;
};

const EmailListTableSkeleton = ()=><TableContainer component={Card}>
  <Table sx={{minWidth: 650}}>
    <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
      <TableRow>
        <EmailListTableHeader id="email">
          דוא"ל
        </EmailListTableHeader>
        <EmailListTableHeader id="name">
          שם
        </EmailListTableHeader>
        <EmailListTableHeader id="created">
          הצטרף ב
        </EmailListTableHeader>
        <EmailListTableHeader id="round" width={200}>
          סיבוב מסרים
        </EmailListTableHeader>
        <EmailListTableHeader width={50}>
          פעולות
        </EmailListTableHeader>
      </TableRow>
    </TableHead>
    <TableBody>
      {Array(3).fill(null).map((__, i)=><TableRow key={i}>
        {Array(5).fill(null).map((__, j)=><TableCell key={j} align="right">
          <Skeleton variant="rounded" width={200} height={30}/>
        </TableCell>)}
      </TableRow>)}
    </TableBody>
  </Table>
</TableContainer>;

const EmailListEmptyState = props=>{
    let {search} = props;
    let theme = useTheme();
    let IconComp = useMemo(()=>search?.length ? PersonSearchIcon : ContactsIcon, [search]);
    let text = useMemo(()=>search?.length
        ? `לא נמצאו משתמשים שתואמים לחיפוש "${search}"`
        : `כאן יופיעו המשתמשים שיתחברו לאפליקציה שלכם`, [search]);
    return <Stack width="100%" direction="column" gap={2} justifyContent="center" alignItems="center" padding={4}>
      <IconComp fontSize="large" sx={{color: theme.palette.text.disabled, height: '64px', width: '64px'}}/>
      <Typography textAlign="center" color="text.secondary">
        {text}
      </Typography>
    </Stack>;
}

const EmailListTable = props=>{
    let {loading, customers, search} = props;
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let [order, setOrder] = useState({});
    let changeOrder = useCallback(col=>setOrder(prev=>{
        if (prev.column!=col)
            return {column: col, direction: 'asc'};
        if (prev.direction=='asc')
            return {column: col, direction: 'desc'};
        return {};
    }), []);
    let orderedCustomers = useMemo(()=>{
        if (!customers?.length||_.isEmpty(order))
            return customers;
        return _.orderBy(customers, [order.column], [order.direction]);
    }, [customers, order]);
    let [addingRounds, setAddingRounds] = useState(new Set());
    let queryClient = useQueryClient();
    let {enqueueSnackbar} = useSnackbar();
    let addCustomerRound = useCallback(async customer=>{
        setAddingRounds(prev=>{
            let val = new Set(prev);
            val.add(customer.id);
            return val;
        });
        try {
            await api.tarotmail.student.addCustomerRound(customer.id);
            queryClient.invalidateQueries({queryKey: ['customers']});
            enqueueSnackbar(`אושר סבב מסרים נוסף עבור ${customer.name}`, {variant: 'success'});
        } catch (e) {
            enqueueSnackbar(e.message, {variant: 'error'});
        } finally {
            setAddingRounds(prev=>{
                let val = new Set(prev);
                val.delete(customer.id);
                return val;
            });
        }
    }, [queryClient, enqueueSnackbar]);
    let openMailStatusModal = useCallback(customer=>openModal({
        header: `סטטוס מיילים עבור ${customer.name}`,
        content: <MailStatusModal customer={customer}/>,
    }), []);
    if (loading)
        return <EmailListTableSkeleton/>;
    if (!orderedCustomers?.length&&sm)
        return <EmailListEmptyState search={search}/>;
    return <TableContainer component={Card}>
      <Table sx={{minWidth: 650}}>
        <TableHead sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
          <TableRow>
            <EmailListTableHeader id="email" orderable
              order={order} changeOrder={changeOrder}>
              דוא"ל
            </EmailListTableHeader>
            <EmailListTableHeader id="name" orderable
              order={order} changeOrder={changeOrder}>
              שם
            </EmailListTableHeader>
            <EmailListTableHeader id="created" orderable
              order={order} changeOrder={changeOrder}>
              הצטרף ב
            </EmailListTableHeader>
            <EmailListTableHeader id="round" orderable
              order={order} changeOrder={changeOrder}
              width={200}>
              סיבוב מסרים
            </EmailListTableHeader>
            <EmailListTableHeader width={50}>
              פעולות
            </EmailListTableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedCustomers?.length ? orderedCustomers.map(customer=><TableRow key={customer.id}>
            <TableCell align="right">
              <Tooltip title={customer.unsubscribed&&'הלקוח הוסר מהשימת התפוצה'}>
                <Typography variant="body2" sx={customer.unsubscribed ? {textDecoration: 'line-through', color: '#CCC'} : {}}
                  onClick={()=>openMailStatusModal(customer)}>
                  {customer.email}
                </Typography>
              </Tooltip>
            </TableCell>
            <TableCell align="right">{customer.name}</TableCell>
            <TableCell align="right">{formatTime(customer.created)}</TableCell>
            <TableCell align="right">{customer.round||'1'}</TableCell>
            <TableCell align="right">
              <Stack direction="row" gap={2}>
                {!customer.eligible&&<SCButton text="הוספת סבב מסרים" icon={<AddCircleOutlineIcon/>}
                  variant="outlined" loading={addingRounds.has(customer.id)} size="small"
                  onClick={()=>openModal({
                      header: `לאשר עוד סבב מסרים עבור ${customer.name}?`,
                      content: <Stack direction="column" gap={2}>
                        <Typography>
                          לאחר האישור, המשתמש יוכל להיכנס שוב ללינק שלכם ולשלוף קלפי מסר לשבוע נוסף
                        </Typography>
                        <Stack direction="row" gap={2} justifyContent="end">
                          <SCButton text="ביטול" variant="outlined" onClick={closeModal}/>
                          <SCButton text="אישור" variant="contained" onClick={()=>{
                              addCustomerRound(customer);
                              closeModal();
                          }}/>
                        </Stack>
                      </Stack>,
                  })}/>}
              </Stack>
            </TableCell>
          </TableRow>) : <TableRow>
            <TableCell colSpan={5}>
              <EmailListEmptyState search={search}/>
            </TableCell>
          </TableRow>}
        </TableBody>
      </Table>
    </TableContainer>;
};

const EmailListTab = ()=>{
    let [customers, loadingCustomers] = useCustomers();
    let [search, setSearch] = useState('');
    let filteredCustomers = useMemo(()=>{
        if (!customers?.length||!search)
            return customers;
        return customers.filter(customer=>[customer.name, customer.email]
            .some(v=>v.toLowerCase().includes(search.toLowerCase())));
    }, [customers, search]);
    let [downloadEmails, downloadingEmails] = useBusyCallback(async ()=>{
        let resp = await api.tarotmail.student.downloadEmails();
        downloadBlob(resp.data, 'emails.csv');
    }, []);
    return <Stack direction="column" gap={2}>
      {!!customers?.length&&<Stack direction="row" gap={2} justifyContent="space-between" flexWrap="wrap">
        <Stack direction="row" gap={2} alignItems="center">
          <SCTextField size="small" placeholder="חיפוש" value={search} onChange={setSearch}/>
        </Stack>
        <Stack direction="row" gap={2} alignItems="center">
          <SCButton variant="outlined" onClick={downloadEmails}
            loading={downloadingEmails} icon={<DownloadIcon size={20}/>}>
            הורדת רשימת מיילים
            </SCButton>
        </Stack>
      </Stack>}
      <EmailListTable customers={filteredCustomers} loading={loadingCustomers} search={search}/>
    </Stack>;
};

const SettingsTabSkeleton = ()=><Stack direction="column" gap={2}>
  <Skeleton variant="rounded" width="90%" height={50}/>
  <Skeleton variant="rounded" width="100%" height={50}/>
  <Stack direction="row" gap={2} justifyContent="space-between">
    <Skeleton variant="rounded" width={300} height={50}/>
    <Skeleton variant="rounded" width={200} height={50}/>
  </Stack>
</Stack>;

const CTA_TYPE_ITEMS = [
    {value: 'url', label: 'מעבר ללינק'},
    {value: 'wa', label: 'שליחת הודעת וואצאפ'},
    {value: 'phonecall', label: 'שיחת טלפון'},
    {value: 'email', label: 'שליחת מייל'},
];

const DEFAULT_CTA = {
    type: 'url',
    text: '',
    url: '',
    waPhone: '',
    waMessage: '',
    phonecallNum: '',
    emailAddr: '',
};

const CtaUrlComp = ({cta, setCta})=><>
  <SCTextField label="לינק" width="100%" sx={{flexGrow: 1}}
    value={cta.url} onChange={v=>setCta(prev=>({...prev, url: v}))}
    placeholder="יכול להיות אתר, מדיה או כל דבר"/>
</>;

const CtaWhatsappComp = ({cta, setCta})=><>
  <SCTextField type="tel"
    value={cta.waPhone} onChange={v=>setCta(prev=>({...prev, waPhone: v}))}
    label="טלפון"
    InputProps={{endAdornment: <Typography sx={{direction: 'ltr', paddingLeft: 2}}>+972</Typography>}}/>
  <SCTextField
    value={cta.waMessage} onChange={v=>setCta(prev=>({...prev, waMessage: v}))}
    label="הודעה שתופיע אוטומטית בוואצאפ (מהלקוח אליכם)"/>
</>;

const CtaPhoneComp = ({cta, setCta})=><>
  <SCTextField type="tel"
    value={cta.phonecallNum} onChange={v=>setCta(prev=>({...prev, phonecallNum: v}))}
    label="טלפון"
    InputProps={{endAdornment: <Typography sx={{direction: 'ltr', paddingLeft: 2}}>+972</Typography>}}/>
</>;

const CtaEmailComp = ({cta, setCta})=><>
  <SCTextField
    value={cta.emailAddr} onChange={v=>setCta(prev=>({...prev, emailAddr: v}))}
    label="כתובת מייל"/>
</>;

const CtaComp = props=>{
    let {value, onChange, textLabel, textHint} = props;
    let CtaTypeComp = useMemo(()=>({
        url: CtaUrlComp,
        wa: CtaWhatsappComp,
        phonecall: CtaPhoneComp,
        email: CtaEmailComp,
    })[value.type||'url']);
    return <Stack direction="column" gap={2}>
      <SCTextField label={textLabel} multiline width="100%" value={value.text}
        onChange={v=>onChange(prev=>({...prev, text: v}))} placeholder={textHint}/>
      <RadioCards label="בלחיצה על טקסט ההנעה לפעולה" value={value.type}
        onChange={v=>onChange(prev=>({...prev, type: v}))} items={CTA_TYPE_ITEMS}/>
      <CtaTypeComp cta={value} setCta={onChange}/>
    </Stack>;
};

const SettingsTab = ()=>{
    let [user] = useUser();
    let [studentSettings, studentLoading] = useStudentSettings();
    let [cta, setCta] = useState(DEFAULT_CTA);
    let [lastCta, setLastCta] = useState(DEFAULT_CTA);
    let {enqueueSnackbar} = useSnackbar();
    useEffect(()=>{
        if (studentSettings?.cta)
            setCta(studentSettings.cta);
        if (studentSettings?.lastCta)
            setLastCta(studentSettings.lastCta);
    }, [studentSettings]);
    let queryClient = useQueryClient();
    let [sendTestMail, sendingTestMail] = useBusyCallback(async ({last})=>{
        try {
            await api.tarotmail.student.sendTestMail({last});
            enqueueSnackbar(`המייל נשלח בהצלחה ל-${user.email}`, {variant: 'success'});
        } catch (e) {
            let msg = 'אירעה שגיאה, נסו שוב מאוחר יותר';
            if (e.status==429)
                msg = 'אפשר להירגע קצת עם שליחת המיילים, נסו שוב מאוחר יותר :)';
            enqueueSnackbar(msg, {variant: 'error'});
        }
    }, [enqueueSnackbar, user]);
    let [save, saving] = useBusyCallback(async ()=>{
        let res = await toastApiErrors(api.tarotmail.student.updateSettings({cta, lastCta}), enqueueSnackbar)
        if (res==API_ERRORS_HANDLED)
            return;
        enqueueSnackbar('ההגדרות נשמרו בהצלחה', {variant: 'success'});
        queryClient.invalidateQueries({queryKey: ['student']});
    }, [queryClient, cta, lastCta, enqueueSnackbar]);
    if (studentLoading)
        return <SettingsTabSkeleton/>;
    return <Stack direction="column" gap={2}>
      <Typography variant="h5">הנעה לפעולה</Typography>
      <Typography>
        יש לך אפשרות להוסיף הצעה מיוחדת שתופיע בתחתית כל מסר יומי ולבחור דרך התקשרות בשביל הלקוח
      </Typography>
      <CtaComp value={cta} onChange={setCta} textLabel="הנעה לפעולה"
        textHint="הטקסט הזה יופיע בתחתית כל מייל, כדאי לכתוב משהו מזמין כמו הזמנה לשירות בהנחה או משהו כזה :)"/>
      <Stack direction="row" gap={2} justifyContent="end">
        <SCButton variant="outlined" onClick={sendTestMail} loading={sendingTestMail}
          icon={<MailOutlineIcon size={20}/>}>
          שליחת מייל לבדיקה
        </SCButton>
      </Stack>
      <Divider/>
      <Typography variant="h5">הנעה לפעולה במייל האחרון</Typography>
      <Typography>
        לאחר 7 ימים של מסרים, יש לך אפשרות לשלוח מייל שמיני עם הצעה מיוחדת ללקוח.<br/>
        למשל: חידוש סבב מסרים, הזמנה לשירות, הזמנה לסדנה וכדומה<br/>
        שימו לב, אם לא תמלאו כאן כלום, המייל השמיני לא ישלח.
      </Typography>
      <CtaComp value={lastCta} onChange={setLastCta} textLabel="הנעה לפעולה"
        textHint="אפשר להציע כאן סבב מסרים נוסף בתשלום או פגישה במחיר מיוחד"/>
      <Stack direction="row" gap={2} justifyContent="end">
        <SCButton variant="outlined" onClick={()=>sendTestMail({last: true})} loading={sendingTestMail}
          icon={<MailOutlineIcon size={20}/>}>
          שליחת מייל לבדיקה
        </SCButton>
      </Stack>
      <Divider/>
      <Stack direction="row" gap={2} justifyContent="space-between">
        <SCButton size="large" variant="contained" onClick={save} loading={saving} icon={<CheckIcon size={20}/>}>
          שמירת הגדרות
        </SCButton>
      </Stack>
    </Stack>;
};

const StatsEventsGraph = ()=>{
    let [{events}={}, loading] = useEvents();
    let [chartEl, setChartEl] = useState();
    useEffect(()=>{
        if (!chartEl||!events?.length)
            return;
        let thisMonth = new Date();
        thisMonth.setDate(1);
        thisMonth.setHours(0);
        thisMonth.setMinutes(0);
        thisMonth.setSeconds(0);
        thisMonth.setMilliseconds(0);
        let lastMonth = new Date(thisMonth);
        lastMonth.setMonth(lastMonth.getMonth()-1);
        let beforeLastMonth = new Date(lastMonth);
        beforeLastMonth.setMonth(beforeLastMonth.getMonth()-1);
        let aggData = [
            {ts: beforeLastMonth, pageViews: 0, subscribes: 0, completions: 0, unsubscribes: 0},
            {ts: lastMonth, pageViews: 0, subscribes: 0, completions: 0, unsubscribes: 0},
            {ts: thisMonth, pageViews: 0, subscribes: 0, completions: 0, unsubscribes: 0},
        ];
        for (let event of events)
        {
            let ts = new Date(event.ts);
            for (let i = aggData.length-1; i>=0; i--)
            {
                if (ts>=aggData[i].ts)
                {
                    switch (event.event)
                    {
                        case 'page_view': aggData[i].pageViews++; break;
                        case 'subscribe': aggData[i].subscribes++; break;
                        case 'complete_flow': aggData[i].completions++; break;
                        case 'unsubscribe': aggData[i].unsubscribes++; break;
                    }
                    break;
                }
            }
        }
        let datasets = [{
            label: 'כניסות לעמוד',
            backgroundColor: 'rgb(152, 79, 125)',
            data: [],
        }, {
            label: 'ערבובי קלפים',
            backgroundColor: 'rgb(68, 123, 194)',
            data: [],
        }, {
            label: 'הרשמות',
            backgroundColor: 'rgb(69, 204, 87)',
            data: [],
        }, {
            label: 'ביטולים',
            backgroundColor: 'rgb(88, 60, 55)',
            data: [],
        }];
        let labels = [];
        for (let month of aggData)
        {
            labels.push(MONTH_NAMES[month.ts.getMonth()]);
            datasets[0].data.push(month.pageViews);
            datasets[1].data.push(month.completions);
            datasets[2].data.push(month.subscribes);
            datasets[3].data.push(month.unsubscribes);
        }
        let c = new Chart(chartEl, {
            type: 'bar',
            data: {labels, datasets},
            options: {
                responsive: true,
                plugins: {legend: {position: 'top'}},
            },
        });
        return ()=>c.destroy();
    }, [chartEl, events]);
    if (loading)
        return <Typography>טוען...</Typography>;
    if (!events?.length)
        return <Typography>לא נמצאו נתונים</Typography>;
    return <canvas ref={setChartEl}></canvas>;
};

const StatsComp = ()=>{
    return <Stack direction="column" gap={2}>
      <StatsEventsGraph/>
    </Stack>;
};

const SubscriptionTab = ()=>{
    let [data, ordersLoading] = useOrders({endTimeOnly: true});
    let navigate = useNavigate();
    return <Stack direction="column" gap={3} alignItems="start">
      {ordersLoading ? <Skeleton
        variant="rounded"
        width={300}
        height={40}
      /> : data ? <Typography>
        המנוי בתוקף עד
        {' '}<strong>{formatDateIsrael(data.endTime)}</strong>
      </Typography> : null}
      <SCButton text="חידוש מנוי" variant="outlined"
        onClick={()=>navigate('/tarotmail/purchase')}/>
    </Stack>;
};

const TUTORIAL_VIDEO_WIDTH = 800;
const TUTORIAL_VIDEO_HEIGHT = 450;

const TutorialTab = ()=>{
    let theme = useTheme();
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let md = useMediaQuery(theme.breakpoints.down('md'));
    let [w, h] = useMemo(()=>{
        if (sm)
            return [TUTORIAL_VIDEO_WIDTH*0.3, TUTORIAL_VIDEO_HEIGHT*0.3];
        if (md)
            return [TUTORIAL_VIDEO_WIDTH*0.5, TUTORIAL_VIDEO_HEIGHT*0.5];
        return [TUTORIAL_VIDEO_WIDTH, TUTORIAL_VIDEO_HEIGHT];
    }, [sm, md]);
    return <Stack justifyContent="center" alignItems="center">
      <Helmet>
        <script src="https://player.vimeo.com/api/player.js"></script>
      </Helmet>
      <iframe
        src="https://player.vimeo.com/video/1145223202?h=29fb8449dd&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
        width={w}
        height={h}
        frameborder="0"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        title="מדריך אפליקציית המסרים"></iframe>
    </Stack>
};

const TABS = {
    EMAIL_LIST: {label: 'רשימת משתמשים', Comp: EmailListTab},
    SETTINGS: {label: 'הגדרות', Comp: SettingsTab},
    STATS: {label: 'סטטיסטיקות', Comp: StatsComp},
    SUBSCRIPTION: {label: 'פרטי מנוי', Comp: SubscriptionTab},
    TUTORIAL: {label: 'סרטון הדרכה', Comp: TutorialTab},
};

const CopyLinkButton = ()=>{
    let {enqueueSnackbar} = useSnackbar();
    let [user, loading] = useUser();
    let onClick = useCallback(()=>{
        let url = user.tarotmailShortUrl;
        if (!url)
        {
            url = CONFIG.DOMAIN;
            if (user.id==SIMONA_USER_ID)
                url += 'tarotmail/simona';
            else
                url += 'tarotmailindex/'+user.id;
        }
        navigator.clipboard.writeText(url);
        enqueueSnackbar('הלינק הועתק ללוח', {variant: 'success'});
    }, [enqueueSnackbar, user]);
    return <SCButton text="העתקת לינק למשתמשים" variant="contained"
      onClick={onClick} color="success"
      skeleton={loading} icon={<LinkIcon fontSize="small"/>}/>;
};

const TabsHeader = props=>{
    let {value, onChange} = props;
    let [user] = useUser();
    let items = useMemo(()=>_.entries(TABS)
        .filter(([k])=>k!='SUBSCRIPTION'||user?.id!=SIMONA_USER_ID)
        .map(([k, v])=>({value: k, label: v.label})), [user]);
    return <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap-reverse">
      <Stack direction="row" flexWrap="wrap" flexGrow={1}>
        {items.map(item=><SCButton key={item.value} text={item.label}
          onClick={()=>onChange(item.value)}
          variant={value==item.value ? 'text' : null}/>)}
      </Stack>
      <Stack direction="row" gap={2}>
        <CopyLinkButton/>
      </Stack>
    </Stack>;
};

const StyledOrderStatusSnackbarContainer = styled(Stack, {shouldForwardProp: p=>!p.startsWith('$')})`
    border-radius: 8px;
    width: 100%;
    border: 2px solid ${p=>p.theme.palette[p.$variant||'success'].main};
`;

const OrderStatusSnackbarContainer = props=>{
    let {variant, IconComp, primaryText, secondaryText, btnText, btnNavigate, onClose} = props;
    let navigate = useNavigate();
    return <StyledOrderStatusSnackbarContainer $variant={variant}
      padding={2} gap={3} direction="row" justifyContent="space-between">
      <Stack direction="row" gap={2} alignItems="center">
        {IconComp&&<IconComp fontSize="large" color={variant}/>}
        <Stack direction="column" gap={1}>
          {primaryText&&<Typography variant="h6">{primaryText}</Typography>}
          {secondaryText&&<Typography color="text.secondary">{secondaryText}</Typography>}
          {btnText&&<Box>
            <SCButton text={btnText} variant="outlined" onClick={()=>navigate(btnNavigate)}/>
          </Box>}
        </Stack>
      </Stack>
      <Stack>
        {onClose&&<IconButton size="small">
          <CloseIcon onClick={onClose}/>
        </IconButton>}
      </Stack>
    </StyledOrderStatusSnackbarContainer>;
};

const ORDER_WARNING_THRESHOLD = 2*WEEK;

const OrderStatusSnackbar = ()=>{
    let [data, loading] = useOrders({endTimeOnly: true});
    let [show, setShow] = useState(true);
    let onClose = useCallback(()=>setShow(false), []);
    let snackbarProps = useMemo(()=>{
        if (loading||!data?.endTime)
            return null;
        let now = new Date();
        let endDate = new Date(data.endTime);
        if (now>endDate)
        {
            return {
                variant: 'error',
                primaryText: 'פג תוקף המנוי',
                secondaryText: 'יש לרכוש מנוי כדי להמשיך להשתמש באפליקציית המסרים',
                btnText: 'לחידוש מנוי',
                btnNavigate: '/tarotmail/purchase',
                IconComp: ErrorOutlineIcon,
                onClose,
            };
        }
        if (new Date(now+ORDER_WARNING_THRESHOLD)>endDate)
        {
            return {
                variant: 'warning',
                primaryText: `תוקף המנוי עומד לפוג ב-${formatDateIsrael(endDate)}`,
                secondaryText: 'יש לחדש את המנוי בהקדם כדי להמשיך להשתמש וליהנות מאפליקציית המסרים',
                btnText: 'לחידוש מנוי',
                btnNavigate: '/tarotmail/purchase',
                IconComp: WarningAmberIcon,
                onClose,
            };
        }
    }, [onClose, loading, data]);
    if (!show||loading)
        return null;
    if (!snackbarProps)
        return null;
    return <OrderStatusSnackbarContainer {...snackbarProps}/>;
};

const TarotmailStudent = ()=>{
    let [tab, setTab] = useState(_.keys(TABS)[0]);
    let Comp = TABS[tab].Comp;
    return <Container maxWidth="lg" sx={{marginTop: 4, marginBottom: 12}}>
      <Stack direction="column" gap={2}>
        <Typography textAlign="center" variant="h2">אפליקציית המסרים</Typography>
        <OrderStatusSnackbar/>
        <TabsHeader value={tab} onChange={setTab}/>
        <Comp/>
      </Stack>
    </Container>;
};

export default withTitle('אפליקציית המסרים', withMainContainer(
    withLoginRequire(withPermGuard(TarotmailStudent, 'tarotmail.student'))));
